import { PatientNote } from "../../../../types/healthcare/PatientNotes";
import type { Role } from "../../../../types/admin/RoleTypes";
import type { UserUuid } from "../../../../types/identity";

/** Strip HTML tags to test whether a rich-text note actually has content. */
export const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, "").trim();

/**
 * A note's tags as a list of display strings, whatever `tags` actually holds.
 *
 * ⚠️ **NOT `note.tags || []`, and that is the whole point.** `patient_notes.tags` is meant to be a
 * flat list of strings, but the Cliniko treatment-note import wrote a keyed provenance OBJECT into
 * it for 436 rows. An object is TRUTHY, so `||` never fell back, `.map` did not exist, and the
 * TypeError took the entire patient profile down through the error boundary. A malformed row is a
 * row that renders no chips — never a clinical record that will not open.
 *
 * Non-string elements are dropped for the same reason one level down: `<Tag key={t}>{t}</Tag>` on
 * an object child throws "Objects are not valid as a React child", so a half-guarded list would
 * white-screen exactly as the object did.
 */
export const noteTags = (note: PatientNote): string[] => (Array.isArray(note.tags) ? note.tags.filter((t): t is string => typeof t === "string") : []);

/** Role name → rank from the RBAC snapshot (GET /practitioner/roles); a role without a rank ranks 0. */
export const roleRanks = (roles: Role[]): Map<string, number> => new Map(roles.map((r) => [r.name, r.rank ?? 0]));

// Parity with healthcare's RoleCatalogue::outranksOrEquals; until the ranks load only the author sees the buttons.
export const canEditNote = (note: PatientNote, userId: UserUuid, userRoles: string[], ranks?: Map<string, number>): boolean => {
	if (note.author_id === userId) return true;
	if (!ranks) return false;
	const highest = userRoles.reduce((max, name) => Math.max(max, ranks.get(name) ?? 0), 0);
	return highest >= (ranks.get(note.author_role ?? "") ?? 0);
};
